import { forwardRef } from "react"
import { Button as EdsButton } from "../../ui/button"
import type { VariantProps } from "class-variance-authority"
import type { buttonVariants } from "../../ui/button"
import { Spinner } from "../../ui/spinner"
import type { LucideIcon } from "lucide-react"

export interface ButtonProps extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
	asChild?: boolean
	/**
	 * Shows a loading spinner and disables the button.
	 * By default, shows spinner alongside the children.
	 */
	loading?: boolean
	icon?: LucideIcon | React.ComponentType<{ className?: string; size?: number | string }>
}

/**
 * Extended Button component with loading state support.
 * This is a wrapper around the EDS Button that adds a loading prop.
 *
 * When loading, shows a spinner before the button text and disables the button.
 *
 * @example Basic usage
 * ```tsx
 * <Button onClick={handleClick}>Click me</Button>
 * ```
 *
 * @example With loading state
 * ```tsx
 * <Button loading={isSubmitting} onClick={handleSubmit}>
 *   Submit
 * </Button>
 * ```
 *
 * @example With variants
 * ```tsx
 * <Button variant="destructive" loading={isDeleting}>
 *   Delete
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	({ icon: Icon, loading, disabled, children, className, ...props }, ref) => {
		return (
			<EdsButton ref={ref} disabled={disabled ?? loading} className={`cursor-pointer ${className || ""}`} {...props}>
				{loading && <Spinner />}
				{Icon && !loading && <Icon />}
				{children}
			</EdsButton>
		)
	}
)

Button.displayName = "Button"
